import type { GeoPoint } from '../geolocation';
import type { TimeSlot } from '../order/order.interface';
import type { RouteLeg } from '../routing';
import type { VehicleProfile } from '../vehicle-template/vehicle-template.interface';

/** What a route optimization engine should minimize across the whole journey. */
export const OPTIMIZATION_OBJECTIVE = { DISTANCE: 'distance', TIME: 'time' } as const;
export type OptimizationObjective = (typeof OPTIMIZATION_OBJECTIVE)[keyof typeof OPTIMIZATION_OBJECTIVE];

/** Narrows an unknown value to a known `OptimizationObjective`. */
export function isOptimizationObjective(value: unknown): value is OptimizationObjective {
  return typeof value === 'string' && Object.values(OPTIMIZATION_OBJECTIVE).includes(value as OptimizationObjective);
}

/** Which leg of a pickup/delivery couple a linked waypoint represents. */
export const OPTIMIZATION_WAYPOINT_ROLE = { DELIVERY: 'delivery', PICKUP: 'pickup' } as const;
export type OptimizationWaypointRole = (typeof OPTIMIZATION_WAYPOINT_ROLE)[keyof typeof OPTIMIZATION_WAYPOINT_ROLE];

/** Constraint kinds a waypoint can carry, as declared in the request. */
export const OPTIMIZATION_CONSTRAINT = { LINK: 'link', TIME_WINDOW: 'time-window' } as const;
export type OptimizationConstraint = (typeof OPTIMIZATION_CONSTRAINT)[keyof typeof OPTIMIZATION_CONSTRAINT];

/** Discriminates the two message shapes sharing the `on-optimization-request` topic: a caller's request, or a deferred engine's self-republished poll tick (see {@link OptimizationPollPayload}). */
export const OPTIMIZATION_MESSAGE_KIND = { POLL: 'poll', REQUEST: 'request' } as const;
export type OptimizationMessageKind = (typeof OPTIMIZATION_MESSAGE_KIND)[keyof typeof OPTIMIZATION_MESSAGE_KIND];

/**
 * What the underlying API supports — not necessarily what our integration exploits yet.
 * Differentiating capabilities across optimization engines, so callers can reason about
 * engines without knowing the provider APIs.
 */
export interface OptimizationEngineCapabilities {
  /** Driver breaks / rest times honored during optimization. */
  driverBreaks: boolean;
  /** Can honor date-specific (absolute, calendar-dated) hard time windows, not just recurring weekday/time availability. */
  hardTimeWindows: boolean;
  /** Fleet optimization across several vehicles. */
  multiVehicle: boolean;
  /** Can return a partial solution with unassigned waypoints instead of failing outright. */
  partialSolutions: boolean;
  /** Pickup-before-delivery ordering constraints between linked waypoints. */
  pickupDeliveryLinks: boolean;
  /** Enforces per-vehicle route duration / availability bounds (`maxRouteDurationInSeconds`, `shift.end`). */
  routeDurationLimits: boolean;
  /** Soft time windows: preferred range + penalty on violation, instead of hard-only windows. */
  softTimeslots: boolean;
  /** Per-waypoint priority honored when assigning/ordering waypoints. */
  waypointPriorities: boolean;
  /** Live/historical traffic considered in travel times. */
  traffic: boolean;
  /** Vehicle load capacity vs per-waypoint demand constraints. */
  vehicleCapacity: boolean;
}

/** Behavior requested for this optimization. A `false` means "do not use the feature", never "the engine must not support it". */
export interface OptimizationConfiguration {
  /** Time windows may be violated with a penalty (true) or must be strictly respected (false). Default: true. */
  softTimeslots?: boolean;
  /** Take traffic into account. Default: true. */
  traffic?: boolean;
}

/** One vehicle participating in the optimization, as submitted in an `OptimizationRequestPayload`. */
export interface OptimizationVehicle {
  /** Named capacity dimensions (e.g. `{ quantity: 120, clients: 30 }`), matched against waypoint `demands`. */
  capacities?: Record<string, number>;
  /** Cost model used by the engine to arbitrate between vehicles/routes. */
  costs?: { fixed?: number; perHour?: number; perKilometer?: number };
  end?: { coordinates: GeoPoint };
  /** Caller-chosen, unique within the request; echoed back on the route. */
  id: string;
  maxRouteDurationInSeconds?: number;
  /** Default: lightweight-vehicle. */
  profile?: VehicleProfile;
  /** ISO 8601 bounds of the vehicle's availability; `start` overrides the request's `departureTime`. */
  shift?: { end?: string; start?: string };
  start: { coordinates: GeoPoint };
}

/** A single waypoint to visit, as submitted in an `OptimizationRequestPayload`. */
export interface OptimizationWaypoint {
  coordinates: GeoPoint;
  /** Per capacity dimension, same keys as vehicle `capacities`. */
  demands?: Record<string, number>;
  /** Caller-chosen, unique within the request; echoed back unchanged in the response. */
  id: string;
  /** Pickup/delivery couple: same `linkId` on exactly 2 waypoints (one pickup, one delivery). The pickup must ALWAYS be sequenced before its delivery. */
  link?: { linkId: string; role: OptimizationWaypointRole };
  serviceTimeInSeconds?: number;
  /** Dates travel as ISO strings over the bus. */
  timeWindows?: TimeSlot[];
}

/**
 * Service Bus payload for the `on-optimization-request` topic.
 * Produced by `pharos-back-deliveries` and consumed by `pharos-back-geo`, which computes the best
 * route(s) for `waypoints` across `vehicles` and always replies on the message's `responseTopic` with
 * an `OptimizationResponsePayload`. A single-vehicle request is the sequencing use case; several
 * vehicles is the full fleet-optimization use case.
 */
export interface OptimizationRequestPayload {
  configuration?: OptimizationConfiguration;
  /** ISO 8601; default: now. Used for vehicles without `shift.start`. */
  departureTime?: string;
  /** Absent (or `'request'`) on every existing producer — see {@link OPTIMIZATION_MESSAGE_KIND}. */
  kind?: typeof OPTIMIZATION_MESSAGE_KIND.REQUEST;
  objective: OptimizationObjective;
  /**
   * Hint for engine selection: when set and the named engine (matched against `metadata.name`) also
   * satisfies the request's required capabilities, it wins over the weight-based arbitration.
   * Ignored (falls back to weight) when absent, or when the named engine doesn't exist or isn't a
   * capability candidate for this request.
   */
  preferredEngine?: string;
  /**
   * Capabilities the selected engine must support: `true` = required. Merged with the capabilities
   * pharos-back-geo derives from the request itself (traffic/soft time windows/multi-vehicle/
   * pickup-delivery links/vehicle capacity — see the engine-routing policy in `pharos-back-geo`). If
   * no registered engine satisfies every required capability, the request fails with
   * `NO_CAPABLE_ENGINE`.
   */
  requiredCapabilities?: Partial<OptimizationEngineCapabilities>;
  waypoints: OptimizationWaypoint[];
  /** At least one. A single vehicle = sequencing use case. */
  vehicles: OptimizationVehicle[];
}

/**
 * Payload for a deferred engine's self-republished poll tick — shares the `on-optimization-request`
 * topic with {@link OptimizationRequestPayload}, discriminated by `kind` (see
 * {@link isOptimizationPollPayload}). Generic to every submit-then-poll engine.
 */
export interface OptimizationPollPayload {
  /** Poll attempt number — `1` on the first poll, incremented on every republish. */
  attempt: number;
  /** Opaque, JSON-serializable blob the engine returned at submit time (e.g. id-mapping tables) and gets back verbatim on every `fetchResult` call. */
  context?: unknown;
  /** Repropagated onto the eventual response sent to `responseTopic`. */
  correlationId?: string;
  /** `metadata.name` of the engine that submitted the job — resolved back to a registered `OptimizationEngine` instance before polling. */
  engine: string;
  /** ISO 8601 timestamp of the FIRST poll attempt — used to detect the poll timeout regardless of how many attempts have run. */
  firstAttemptAt: string;
  kind: typeof OPTIMIZATION_MESSAGE_KIND.POLL;
  /** Id returned by the provider at submit time, passed back to `engine.fetchResult()`/`engine.cancelJob()`. */
  providerJobId: string;
  /** Topic the eventual `OptimizationResponsePayload` (success or error) is sent to — same topic the original request carried. */
  responseTopic: string;
}

/**
 * Narrows a message payload carried on `on-optimization-request` to the poll shape. Called before
 * structural validation, so it must tolerate `null`/non-object input without throwing.
 */
export function isOptimizationPollPayload(
  payload: OptimizationRequestPayload | OptimizationPollPayload,
): payload is OptimizationPollPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'kind' in payload &&
    payload.kind === OPTIMIZATION_MESSAGE_KIND.POLL
  );
}

/** One stop placed in its route's visiting order. */
export interface OptimizedWaypoint {
  /**
   * ISO 8601 estimated arrival at the waypoint, including any waiting before service. `RouteLeg`
   * durations remain travel-only, so this (and `estimatedDepartureTime`) is what carries waiting time.
   */
  estimatedArrivalTime?: string;
  /** ISO 8601 estimated departure after service (arrival + wait + service time). */
  estimatedDepartureTime?: string;
  /**
   * Constraint kinds the engine reports as satisfied for this waypoint: `time-window` when one of the
   * waypoint's `timeWindows` was met, `link` when the waypoint is on the right side of its pickup/delivery
   * couple. Omitted when the engine reports none.
   */
  fulfilledConstraints?: OptimizationConstraint[];
  id: string;
  /** 0-based within its route; vehicle start/end excluded. */
  sequence: number;
}

/** One vehicle's computed route. */
export interface OptimizationRoute {
  /** ISO 8601 — departure anchoring `legs` in absolute time. */
  departureTime?: string;
  /**
   * legs[i] = travel between waypoint i and i+1 in route order (vehicle start → waypoints… → end when the
   * vehicle has one). Present only when the engine returned per-leg travel data; `polyline` only when
   * it returned geometry.
   */
  legs?: RouteLeg[];
  /** Sorted by `sequence`. */
  waypoints: OptimizedWaypoint[];
  totalDistanceInMeters?: number;
  totalTimeInSeconds?: number;
  vehicleId: string;
}

/** A waypoint the engine could not assign to any route. */
export interface OptimizationUnassignedWaypoint {
  id: string;
  /** Engine-provided explanation for why the waypoint was left unassigned, when available. */
  reason?: string;
}

/** A successfully computed set of routes for the submitted waypoints/vehicles. */
export interface OptimizationSuccessPayload {
  /** Name of the engine that produced this result. */
  engine: string;
  routes: OptimizationRoute[];
  totalDistanceInMeters?: number;
  totalTimeInSeconds?: number;
  unassignedWaypoints?: OptimizationUnassignedWaypoint[];
}

/**
 * Optimization error code constants.
 * - ENGINE_CALL_FAILED: the optimization engine could not be reached or returned an unexpected error.
 * - ENGINE_TIMEOUT: a deferred engine's job never reached a terminal state before the poll deadline — polling was abandoned.
 * - INVALID_REQUEST: the request payload failed validation (e.g. malformed links, unsupported time windows).
 * - NO_CAPABLE_ENGINE: no registered engine supports every required capability.
 * - NO_SOLUTION_FOUND: the engine could not compute any route satisfying the constraints.
 * - TOO_MANY_WAYPOINTS: the request exceeds the engine's maximum supported waypoint count.
 */
export const OPTIMIZATION_ERROR_CODE = {
  ENGINE_CALL_FAILED: 'ENGINE_CALL_FAILED',
  ENGINE_TIMEOUT: 'ENGINE_TIMEOUT',
  INVALID_REQUEST: 'INVALID_REQUEST',
  NO_CAPABLE_ENGINE: 'NO_CAPABLE_ENGINE',
  NO_SOLUTION_FOUND: 'NO_SOLUTION_FOUND',
  TOO_MANY_WAYPOINTS: 'TOO_MANY_WAYPOINTS',
} as const;
export type OptimizationErrorCode = (typeof OPTIMIZATION_ERROR_CODE)[keyof typeof OPTIMIZATION_ERROR_CODE];

export interface OptimizationErrorPayload {
  code: OptimizationErrorCode;
  details?: Record<string, unknown>;
  message: string;
}

/** Service Bus payload for the `on-optimization-response` topic (sent to the request's `responseTopic`). */
export type OptimizationResponsePayload = OptimizationErrorPayload | OptimizationSuccessPayload;
